<template>
  <Modal title="" :max-width="720" :header-border="false" body-overflow="auto" body-max-height="600px" :body-padding="false" @close="onClose">
    <template #header>
      <div class="cd__title-wrap">
        <span class="cd-source">
          <span class="cd-source__mark" style="background: #0d9dda; color: #fff;">
            <svg viewBox="0 0 16 16" fill="currentColor"><path d="M6.5 4.2a3 3 0 015.4 1 2.4 2.4 0 011.6 4.3 2.6 2.6 0 01-3 2 3.1 3.1 0 01-5.2-.4 2.3 2.3 0 01-2-3.9 2.7 2.7 0 013.2-3z"/></svg>
          </span>
          Salesforce
        </span>
        <div class="cd__name">
          {{ record.Name || 'Opportunity' }}
          <a v-if="oppUrl" :href="oppUrl" target="_blank" rel="noopener" class="cd-arrow" title="Open in Salesforce"><AppIcon name="arrow_up_right" :size="14" /></a>
        </div>
        <div class="cd__sub">
          <span v-if="record.Type">{{ record.Type }}</span>
          <span v-if="record.Type && record.StageName" class="cd__dot" />
          <span v-if="record.StageName">{{ record.StageName }}</span>
          <span v-if="ownerName && (record.Type || record.StageName)" class="cd__dot" />
          <span v-if="ownerName">{{ ownerName }}</span>
        </div>
      </div>
    </template>

    <!-- BODY -->
    <div class="cd-cols">
      <!-- LEFT -->
      <div class="cd-cols__l">
        <!-- Hero metric -->
        <div class="cd-metrics">
          <div class="cd-metric cd-metric--hero">
            <span class="cd-metric__k">Amount</span>
            <span class="cd-metric__v">{{ formattedAmount }}</span>
          </div>
          <div class="cd-vrule" />
          <div class="cd-metric">
            <span class="cd-metric__k">Close date</span>
            <span class="cd-metric__v">{{ record.CloseDate || '—' }}</span>
          </div>
          <div v-if="record.Probability != null" class="cd-metric">
            <span class="cd-metric__k">Probability</span>
            <span class="cd-metric__v">{{ record.Probability }}%</span>
          </div>
        </div>

        <!-- Stage bar -->
        <div v-if="record.StageName">
          <div class="cd-bar">
            <div class="cd-bar__seg is-current" />
          </div>
          <div class="cd-barmeta">
            <span class="cd-barmeta__cur">{{ record.StageName }}</span>
          </div>
        </div>

        <!-- Custom fields -->
        <div v-if="customFields.length" class="cd-notes">
          <div v-for="cf in customFields" :key="cf.k" class="cd-note">
            <span class="cd-note__k">{{ cf.k }}</span>
            <span v-if="cf.v" class="cd-note__v">{{ cf.v }}</span>
            <span v-else class="cd-note__empty">—</span>
          </div>
        </div>

        <!-- Standard extra fields -->
        <div v-if="extraFields.length" class="cd-fields">
          <div v-for="f in extraFields" :key="f.k" class="cd-field">
            <span class="cd-field__k">{{ f.k }}</span>
            <span class="cd-field__v">{{ f.v }}</span>
          </div>
        </div>
      </div>

      <!-- RIGHT -->
      <div class="cd-cols__r">
        <!-- Company -->
        <div v-if="company">
          <div class="cd-label">Account</div>
          <div class="cd-assoc">
            <CompanyLogo :domain="company.domain" :name="company.name" aria-hidden="true" />
            <div style="flex: 1; min-width: 0;">
              <div class="cd-assoc__v">
                {{ company.name }}
                <a v-if="accountUrl" :href="accountUrl" target="_blank" rel="noopener" class="cd-arrow" title="Open in Salesforce"><AppIcon name="arrow_up_right" :size="12" /></a>
              </div>
            </div>
          </div>
        </div>

        <!-- Contact Roles -->
        <div v-if="contactRoles.length">
          <div class="cd-label">Contact Roles · {{ contactRoles.length }}</div>
          <div class="cd-contacts">
            <div v-for="c in contactRoles" :key="c.ini" class="cd-contact">
              <span class="cd-avatar">{{ c.ini }}</span>
              <div class="cd-contact__main">
                <div class="cd-contact__name">
                  {{ c.name }}
                  <a v-if="c.url" :href="c.url" target="_blank" rel="noopener" class="cd-arrow" title="Open in Salesforce"><AppIcon name="arrow_up_right" :size="11" /></a>
                </div>
                <div class="cd-contact__title">{{ c.title }}</div>
              </div>
              <span v-if="c.role" class="cd-contact__role" :class="{ 'is-key': c.key }">{{ c.role }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <button class="cd-btn cd-btn--ghost" @click="onClose">Close</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, toRef } from 'vue'
import Modal from '../../next/components/Modal.vue'
import AppIcon from '../../components/AppIcon.vue'
import CompanyLogo from '../../next/components/CompanyLogo.vue'
import { getSalesforceUrl } from '../../services/salesforce'
import { useAccountBasics } from '../../hooks/useAccountBasics'

const props = defineProps<{
  record: Record<string, any>
  accountId?: string
  onClose: () => void
}>()

const accountIdRef = computed(() => props.accountId || '')
const { basics } = useAccountBasics(toRef(accountIdRef))

const oppUrl = computed(() => getSalesforceUrl({ Id: props.record?.Id }, 'Opportunity'))
const ownerName = computed(() => String(props.record?.Owner?.Name || '').trim())
const accountUrl = computed(() => {
  const sfId = basics.value?.salesforce_account_id
  return sfId ? getSalesforceUrl({ Id: sfId }, 'Account') : null
})

const formattedAmount = computed(() => {
  const v = props.record?.Amount
  if (v == null) return '—'
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v)
  } catch {
    return `$${v}`
  }
})

// ── Contact Roles ────────────────────────────────────────────────

const KEY_ROLES = new Set(['decision maker', 'champion', 'economic buyer', 'executive sponsor'])

const contactRoles = computed(() => {
  const raw = props.record?.OpportunityContactRoles
  if (!raw) return []
  const records = Array.isArray(raw) ? raw : raw?.records
  if (!Array.isArray(records)) return []
  return records.map((r: any) => {
    const name = String(r.Contact?.Name || '')
    const role = String(r.Role || '')
    const contactId = String(r.ContactId || '')
    return {
      ini: name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase(),
      name,
      title: String(r.Contact?.Title || ''),
      role,
      key: KEY_ROLES.has(role.toLowerCase()),
      url: contactId ? getSalesforceUrl({ Id: contactId }, 'Contact') : null,
    }
  })
})

// ── Custom fields (long text → notes, short → field grid) ───────

const SKIP = new Set([
  'attributes', 'OpportunityContactRoles', 'Id', 'Name', 'StageName',
  'Amount', 'CloseDate', 'Probability', 'Type', 'IsClosed', 'IsWon',
  'OwnerId', 'Owner',
  'CreatedDate', 'LastModifiedDate', 'AccountId', 'LeadSource',
  'ForecastCategoryName',
])

const customFields = computed(() => {
  return Object.entries(props.record || {})
    .filter(([k, v]) => !SKIP.has(k) && v != null && String(v).length > 100)
    .map(([k, v]) => ({
      k: k.replace(/__c$/, '').replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, c => c.toUpperCase()),
      v: String(v),
    }))
})

const extraFields = computed(() => {
  const show: { k: string; v: string }[] = []
  const r = props.record || {}
  if (r.LeadSource) show.push({ k: 'Lead Source', v: r.LeadSource })
  if (r.ForecastCategoryName) show.push({ k: 'Forecast', v: r.ForecastCategoryName })
  // Short custom fields
  Object.entries(r)
    .filter(([k, v]) => !SKIP.has(k) && v != null && String(v).length > 0 && String(v).length <= 100)
    .forEach(([k, v]) => {
      show.push({
        k: k.replace(/__c$/, '').replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b\w/g, c => c.toUpperCase()),
        v: String(v),
      })
    })
  return show
})

// ── Company ─────────────────────────────────────────────────────

const company = computed(() => {
  const b = basics.value
  if (!b) return null
  return { name: b.name || b.domain, domain: b.domain }
})
</script>
